import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import './SystemHealth.css';

const SystemHealth = () => {
  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('1h');
  const [metrics, setMetrics] = useState({
    lambda: null,
    dynamodb: null,
    s3: null,
    cloudfront: null
  });

  const timeRangeOptions = [
    { value: '1h', label: '1 Hour' },
    { value: '6h', label: '6 Hours' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' }
  ];

  const fetchLambdaMetrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/analytics/lambda?timeRange=${timeRange}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching Lambda metrics:', error);
      return null;
    }
  };

  const fetchDynamoDBMetrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/analytics/dynamodb?timeRange=${timeRange}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching DynamoDB metrics:', error);
      return null;
    }
  };

  const fetchS3Metrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/analytics/s3?timeRange=${timeRange}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching S3 metrics:', error);
      return null;
    }
  };

  const fetchCloudFrontMetrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/analytics/cloudfront?timeRange=${timeRange}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching CloudFront metrics:', error);
      return null;
    }
  };

  const fetchSystemMetrics = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all metrics in parallel
      const [lambdaData, dynamodbData, s3Data, cloudfrontData] = await Promise.all([
        fetchLambdaMetrics(),
        fetchDynamoDBMetrics(),
        fetchS3Metrics(),
        fetchCloudFrontMetrics()
      ]);
      
      setMetrics({
        lambda: lambdaData,
        dynamodb: dynamodbData,
        s3: s3Data,
        cloudfront: cloudfrontData
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemMetrics();
  }, [timeRange]);

  const formatDuration = (ms) => {
    if (!ms) return '0ms';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getHealthStatus = (errorRate) => {
    if (errorRate === 0) return 'healthy';
    if (errorRate < 1) return 'warning';
    return 'critical';
  };

  const renderLambdaSection = () => {
    if (!metrics.lambda || !metrics.lambda.metrics) {
      return (
        <div className="service-section">
          <div className="service-header">
            <h3>🔧 Lambda</h3>
            <div className="health-indicator unavailable">❌</div>
          </div>
          <div className="metric-unavailable">
            <p>Metrics unavailable</p>
            <small>Check CloudWatch permissions or deploy backend</small>
          </div>
        </div>
      );
    }

    const { duration, invocations, errors, coldStarts } = metrics.lambda.metrics;
    const errorRate = (invocations?.total > 0) ? ((errors?.total || 0) / invocations.total) * 100 : 0;
    const coldStartRate = (invocations?.total > 0) ? ((coldStarts?.total || 0) / invocations.total) * 100 : 0;
    const healthStatus = getHealthStatus(errorRate);

    return (
      <div className="service-section">
        <div className="service-header">
          <h3>🔧 Lambda</h3>
          <div className={`health-indicator ${healthStatus}`}>
            {healthStatus === 'healthy' && '✅'}
            {healthStatus === 'warning' && '⚠️'}
            {healthStatus === 'critical' && '🚨'}
          </div>
        </div>
        
        <div className="service-metrics">
          <div className="metric-item">
            <div className="metric-value">{formatNumber(invocations?.total || 0)}</div>
            <div className="metric-label">Total Invocations</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">{formatDuration(duration?.average || 0)}</div>
            <div className="metric-label">Avg Duration</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">{errors?.total || 0}</div>
            <div className="metric-label">Errors</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">{errorRate.toFixed(2)}%</div>
            <div className="metric-label">Error Rate</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{color: coldStarts?.total === 0 ? '#059669' : '#dc2626'}}>
              {coldStarts?.total || 0}
            </div>
            <div className="metric-label">Cold Starts</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{color: coldStartRate === 0 ? '#059669' : '#dc2626'}}>
              {coldStartRate.toFixed(2)}%
            </div>
            <div className="metric-label">Cold Start Rate</div>
          </div>
        </div>
      </div>
    );
  };

  const renderDynamoDBSection = () => {
    if (!metrics.dynamodb || !metrics.dynamodb.metrics) {
      return (
        <div className="service-section">
          <div className="service-header">
            <h3>🗄️ DynamoDB</h3>
            <div className="health-indicator unavailable">❌</div>
          </div>
          <div className="metric-unavailable">
            <p>Metrics unavailable</p>
            <small>Deploy backend to enable DynamoDB metrics</small>
          </div>
        </div>
      );
    }

    const { readCapacity, writeCapacity, throttles } = metrics.dynamodb.metrics;
    const healthStatus = (throttles?.total > 0) ? 'warning' : 'healthy';

    return (
      <div className="service-section">
        <div className="service-header">
          <h3>🗄️ DynamoDB</h3>
          <div className={`health-indicator ${healthStatus}`}>
            {healthStatus === 'healthy' && '✅'}
            {healthStatus === 'warning' && '⚠️'}
          </div>
        </div>
        
        <div className="service-metrics">
          <div className="metric-item">
            <div className="metric-value">{formatNumber(readCapacity?.total || 0)}</div>
            <div className="metric-label">Read Capacity Used</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">{formatNumber(writeCapacity?.total || 0)}</div>
            <div className="metric-label">Write Capacity Used</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">{(readCapacity?.average || 0).toFixed(2)}</div>
            <div className="metric-label">Avg Read/Min</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">{(writeCapacity?.average || 0).toFixed(2)}</div>
            <div className="metric-label">Avg Write/Min</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">{throttles?.total || 0}</div>
            <div className="metric-label">Throttles</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value table-name">{metrics.dynamodb?.tableName || 'Unknown'}</div>
            <div className="metric-label">Table</div>
          </div>
        </div>
      </div>
    );
  };

  const renderS3Section = () => {
    if (!metrics.s3 || !metrics.s3.metrics) {
      return (
        <div className="service-section">
          <div className="service-header">
            <h3>🪣 S3 Storage</h3>
            <div className="health-indicator unavailable">❌</div>
          </div>
          <div className="metric-unavailable">
            <p>Metrics unavailable</p>
            <small>Deploy backend to enable S3 metrics</small>
          </div>
        </div>
      );
    }

    const { objects, size } = metrics.s3.metrics;

    return (
      <div className="service-section">
        <div className="service-header">
          <h3>🪣 S3 Storage</h3>
          <div className="health-indicator healthy">✅</div>
        </div>
        
        <div className="service-metrics">
          <div className="metric-item">
            <div className="metric-value">{formatNumber(objects?.current || 0)}</div>
            <div className="metric-label">Objects</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">{metrics.s3?.summary?.bucketSize || '0 B'}</div>
            <div className="metric-label">Storage Used</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value bucket-name">{metrics.s3?.bucketName || 'Unknown'}</div>
            <div className="metric-label">Bucket</div>
          </div>
        </div>
      </div>
    );
  };

  const renderCloudFrontSection = () => {
    if (!metrics.cloudfront || !metrics.cloudfront.metrics) {
      return (
        <div className="service-section">
          <div className="service-header">
            <h3>🌐 CloudFront</h3>
            <div className="health-indicator unavailable">❌</div>
          </div>
          <div className="metric-unavailable">
            <p>Metrics unavailable</p>
            <small>Deploy backend to enable CloudFront metrics</small>
          </div>
        </div>
      );
    }

    const { 
      requests, 
      bytesDownloaded, 
      bytesUploaded,
      cacheHitRate, 
      cacheMissRate,
      originLatency, 
      errorRate, 
      serverErrorRate,
      totalErrorRate,
      requestRate,
      successRate
    } = metrics.cloudfront.metrics;
    
    const hasData = requests?.total > 0 || bytesDownloaded?.total > 0;
    
    // If no data, show a different message
    if (!hasData) {
      return (
        <div className="service-section">
          <div className="service-header">
            <h3>🌐 CloudFront</h3>
            <div className="health-indicator healthy">⏳</div>
          </div>
          <div className="metric-unavailable">
            <p>No recent activity</p>
            <small>CloudFront metrics may take 15+ minutes to appear</small>
          </div>
        </div>
      );
    }

    // Health status based on server errors (5xx) being most critical
    const serverErrorAvg = serverErrorRate?.average || 0;
    const clientErrorAvg = errorRate?.average || 0;
    const healthStatus = serverErrorAvg > 1 ? 'critical' : 
                        (serverErrorAvg > 0.1 || clientErrorAvg > 5) ? 'warning' : 'healthy';

    return (
      <div className="service-section">
        <div className="service-header">
          <h3>🌐 CloudFront</h3>
          <div className={`health-indicator ${healthStatus}`}>
            {healthStatus === 'healthy' && '✅'}
            {healthStatus === 'warning' && '⚠️'}
            {healthStatus === 'critical' && '🚨'}
          </div>
        </div>
        
        <div className="service-metrics">
          <div className="metric-item">
            <div className="metric-value">{formatNumber(requests?.total || 0)}</div>
            <div className="metric-label">Total Requests</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{
              color: successRate?.average === null ? '#64748b' :
                     (successRate?.average || 0) > 95 ? '#059669' : 
                     (successRate?.average || 0) > 90 ? '#f59e0b' : '#dc2626'
            }}>
              {successRate?.average === null ? 'N/A' : `${(successRate.average || 0).toFixed(1)}%`}
            </div>
            <div className="metric-label">Success Rate</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{
              color: serverErrorRate?.average === null ? '#64748b' :
                     (serverErrorRate?.average || 0) === 0 ? '#059669' : 
                     (serverErrorRate?.average || 0) < 1 ? '#f59e0b' : '#dc2626'
            }}>
              {serverErrorRate?.average === null ? 'N/A' : `${(serverErrorRate.average || 0).toFixed(2)}%`}
            </div>
            <div className="metric-label">Server Errors (5xx)</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{
              color: totalErrorRate?.average === null ? '#64748b' :
                     (totalErrorRate?.average || 0) === 0 ? '#059669' : 
                     (totalErrorRate?.average || 0) < 2 ? '#f59e0b' : '#dc2626'
            }}>
              {totalErrorRate?.average === null ? 'N/A' : `${(totalErrorRate.average || 0).toFixed(2)}%`}
            </div>
            <div className="metric-label">Total Error Rate</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">
              {bytesDownloaded?.total ? formatBytes(bytesDownloaded.total) : '0 B'}
            </div>
            <div className="metric-label">Downloaded</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value">
              {bytesUploaded?.total ? formatBytes(bytesUploaded.total) : '0 B'}
            </div>
            <div className="metric-label">Uploaded</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{
              color: cacheHitRate?.average === null ? '#64748b' :
                     (cacheHitRate?.average || 0) > 80 ? '#059669' : 
                     (cacheHitRate?.average || 0) > 50 ? '#f59e0b' : '#dc2626'
            }}>
              {cacheHitRate?.average === null ? 'N/A' : `${(cacheHitRate.average || 0).toFixed(1)}%`}
            </div>
            <div className="metric-label">Cache Hit Rate</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{
              color: cacheMissRate?.average === null ? '#64748b' :
                     (cacheMissRate?.average || 0) < 20 ? '#059669' : 
                     (cacheMissRate?.average || 0) < 50 ? '#f59e0b' : '#dc2626'
            }}>
              {cacheMissRate?.average === null ? 'N/A' : `${(cacheMissRate.average || 0).toFixed(1)}%`}
            </div>
            <div className="metric-label">Cache Miss Rate</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{color: originLatency?.average === null ? '#64748b' : '#2563eb'}}>
              {originLatency?.average === null ? 'N/A' : `${(originLatency.average || 0).toFixed(0)}ms`}
            </div>
            <div className="metric-label">Origin Latency</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value" style={{color: requestRate?.average === null ? '#64748b' : '#2563eb'}}>
              {requestRate?.average === null ? 'N/A' : `${(requestRate.average || 0).toFixed(3)}/min`}
            </div>
            <div className="metric-label">Request Rate</div>
          </div>
          
          <div className="metric-item">
            <div className="metric-value bucket-name">
              {metrics.cloudfront?.distributionId ? 
                metrics.cloudfront.distributionId.substring(0, 8) + '...' : 
                'Unknown'
              }
            </div>
            <div className="metric-label">Distribution</div>
          </div>
        </div>
      </div>
    );
  };

  const renderPlaceholderSection = (title, icon, description) => (
    <div className="service-section placeholder">
      <div className="service-header">
        <h3>{icon} {title}</h3>
        <div className="health-indicator unavailable">⏳</div>
      </div>
      <div className="metric-unavailable">
        <p>Coming Soon</p>
        <small>{description}</small>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="system-health">
        <div className="system-health-header">
          <h2>🏥 System Health</h2>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading system metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="system-health">
      <div className="system-health-header">
        <h2>🏥 System Health</h2>
        <div className="system-health-controls">
          <div className="time-range-selector">
            <label htmlFor="timeRange">Time Range:</label>
            <select
              id="timeRange"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="time-range-select"
            >
              {timeRangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button 
            onClick={fetchSystemMetrics} 
            className="refresh-button"
            disabled={loading}
          >
            {loading ? '🔄' : '↻'} Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>⚠️ Error loading metrics: {error}</p>
          <button onClick={fetchSystemMetrics} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className="services-container">
        {renderLambdaSection()}
        
        {renderDynamoDBSection()}
        
        {renderS3Section()}
        
        {renderCloudFrontSection()}
        
        {renderPlaceholderSection(
          'Application Metrics',
          '📱',
          'User activity, feature usage, performance'
        )}
      </div>
    </div>
  );
};

export default SystemHealth;